import React, { useState } from "react";
import {
  IoCloseOutline,
  IoChevronDownOutline,
  IoFlameOutline,
  IoHeadsetOutline,
} from "react-icons/io5";
import {
  RiBarChartLine,
  RiCoinLine,
} from "react-icons/ri";
import {
  MdOutlineDashboard,
  MdOutlineAccountBalanceWallet,
  MdOutlineSwapHoriz,
} from "react-icons/md";
import { TbReportMoney } from "react-icons/tb";
import { NavLink } from "react-router-dom";

import logo from "../../assests/images/weblogo.png";
import { useTranslation } from "react-i18next";
import { FaUser } from "react-icons/fa";

// ─── Component ────────────────────────────────────────────────────────────────
const SideBar = ({ setShowSiderbar }) => {
  const { t } = useTranslation();
  const {
    Wallet,
    Crypto,
    Market,
    TrendingCoin
  } = t("UserSidebar", { returnObjects: true });

  const [openDropdowns, setOpenDropdowns] = useState({});

  const toggleDropdown = (url) => {
    setOpenDropdowns((prev) => ({ ...prev, [url]: !prev[url] }));
  };

  const navSections = [
    {
      label: "NAVIGATION",
      items: [
        {
          reactIcon: <MdOutlineDashboard size={18} />,
          title: "Dashboard",
          url: "dashboard",
          subtitle: "Overview of your account",
        },
        {
          reactIcon: <MdOutlineAccountBalanceWallet size={18} />,
          title: Wallet,
          url: "wallet",
          subtitle: "Holdings & balances",
        },
        {
          reactIcon: <RiBarChartLine size={18} />,
          title: Market,
          url: "market",
          subtitle: "Live prices & trends",
        },
        {
          reactIcon: <IoFlameOutline size={18} />,
          title: TrendingCoin,
          url: "trendingcoins",
          subtitle: "What's hot",
        },

        {
          reactIcon: <TbReportMoney size={18} />,
          title: "Transactions",
          url: "transaction",
          subtitle: "History & details",
        },
        {
          reactIcon: <MdOutlineSwapHoriz size={18} />,
          title: "Exchange",
          url: "swapcrypto",
          subtitle: "Swap crypto",
        },
      ],
    },
    {
      label: "QUICK ACCESS",
      items: [
        {
          reactIcon: <IoHeadsetOutline size={18} />,
          title: "Support",
          url: "support",
          subtitle: "Get help",
        },
        {
          reactIcon: <FaUser size={18} />,
          title: "Account Access",
          url: "profile",
          subtitle: "Settings & profile",
        },
      ],
    },
  ];

  return (
    <div className="
      flex flex-col h-full w-64
      bg-[#0e1525] dark:bg-[#020617]
      border-r border-white/5
      overflow-y-auto py-10
      shadow-[10px_0_40px_rgba(0,0,0,.75)]
      rounded-tr-[30px] rounded-br-[30px]
    ">

      {/* ── Logo ── */}
      <div className="pt-6 pb-2 px-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={logo} alt="elementuslabs Logo" className="h-11 w-auto object-contain" />
        </div>
        <button
          className="xl:hidden text-white/80 hover:text-white transition-colors"
          onClick={() => setShowSiderbar?.(false)}
        >
          <IoCloseOutline size={22} />
        </button>
      </div>

      {/* ── Sections ── */}
      <nav className="flex-1 px-3 mt-4 space-y-7 pb-4">
        {navSections.map((section) => (
          <div key={section.label}>

            {/* Section label */}
            <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.15em] text-white/80 uppercase">
              {section.label}
            </p>

            {/* Items */}
            <ul className="space-y-3">
              {section.items.map((item) => (
                <li key={item.url}>

                  {/* ── Item with dropdown ── */}
                  {item.isDropdown ? (
                    <div>
                      {/* Parent button */}
                      <button
                        onClick={() => toggleDropdown(item.url)}
                        className={`
                          group relative w-full flex items-center gap-3 px-3 py-[10px] rounded-xl
                          cursor-pointer select-none transition-all duration-200
                          ${openDropdowns[item.url] ? "border-[1px] border-[#01d28c]" : "border-0"}
                        `}
                        style={{
                          background: openDropdowns[item.url]
                            ? "radial-gradient(circle at 0 0,rgba(24,226,143,.25),transparent 55%),rgba(15,23,42,.98)"
                            : "#0b1629",
                        }}
                      >
                        {/* Icon box */}
                        <span className={`
                          flex-shrink-0 flex items-center justify-center
                          w-9 h-9 rounded-xl transition-all duration-200
                          ${openDropdowns[item.url] ? "bg-[#0c1129]" : "bg-[#1a2540] group-hover:bg-[#1e2d47]"}
                        `}>
                          <span className={
                            openDropdowns[item.url]
                              ? "text-[#01d28c]"
                              : "text-white/80 group-hover:text-white/90"
                          }>
                            {item.reactIcon}
                          </span>
                        </span>

                        {/* Title */}
                        <span className={`
                          flex-1 text-left text-[12px] font-medium leading-tight
                          ${openDropdowns[item.url] ? "text-white" : "text-white/80 group-hover:text-white/90"}
                        `}>
                          {item.title}
                        </span>

                        {/* Subtitle */}
                        <span className={`
                          text-[10px] italic text-white/50
                          ${openDropdowns[item.url] ? "text-white/70" : "text-white/80"}
                        `}>
                          {item.subtitle}
                        </span>

                        {/* Chevron */}
                        <IoChevronDownOutline
                          size={13}
                          className={`
                            text-white/80 transition-transform duration-200 flex-shrink-0
                            ${openDropdowns[item.url] ? "rotate-180 text-white/60" : ""}
                          `}
                        />
                      </button>

                      {/* Dropdown children */}
                      {openDropdowns[item.url] && (
                        <ul className="mt-1 ml-[52px] space-y-1">
                          {item.dropdownItems.map((child) => (
                            <li key={child.url}>
                              <NavLink
                                to={child.url}
                                className={({ isActive }) => `
                                  flex items-center gap-2 px-3 py-2 rounded-lg
                                  cursor-pointer select-none transition-all duration-150 text-[11px] font-medium
                                  ${isActive
                                    ? "text-[#01d28c] bg-[#01d28c]/8"
                                    : "text-white/50 hover:text-white/80 hover:bg-white/5"
                                  }
                                `}
                              >
                                {({ isActive }) => (
                                  <>
                                    <span className={`flex-shrink-0 ${isActive ? "text-[#01d28c]" : "text-white/80"}`}>
                                      {child.reactIcon}
                                    </span>
                                    {child.title}
                                  </>
                                )}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                  ) : (
                    /* ── Regular NavLink ── */
                    <NavLink
                      to={item.url}
                      end={item.url === "dashboard"}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 px-3 py-[10px] rounded-xl
                         cursor-pointer select-none transition-all duration-200
                         ${isActive ? "border-[1px] border-[#01d28c]" : "border-0"}`
                      }
                      style={({ isActive }) => ({
                        background: isActive
                          ? "radial-gradient(circle at 0 0,rgba(24,226,143,.25),transparent 55%),rgba(15,23,42,.98)"
                          : "#0b1629",
                      })}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-green-400" />
                          )}

                          {/* Icon box */}
                          <span className={`
                            flex-shrink-0 flex items-center justify-center
                            w-9 h-9 rounded-xl transition-all duration-200
                            ${isActive ? "bg-[#0c1129]" : "bg-[#1a2540] group-hover:bg-[#1e2d47]"}
                          `}>
                            <span className={isActive ? "text-[#01d28c]" : "text-white/80 group-hover:text-white/90"}>
                              {item.reactIcon}
                            </span>
                          </span>

                          {/* ✅ FIX: Title + Subtitle dono show ho rahe hain */}
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={`
                              text-[12px] font-medium leading-tight
                              ${isActive ? "text-white" : "text-white/80 group-hover:text-white/90"}
                            `}>
                              {item.title}
                            </span>
                            {item.subtitle && (
                              <span className={`
                                text-[10px] italic leading-tight mt-[2px]
                                ${isActive ? "text-white/60" : "text-white/35 group-hover:text-white/50"}
                              `}>
                                {item.subtitle}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </NavLink>
                  )}

                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
};

export default SideBar;